import { Block } from '@/core/Block'

/**
 * Wires Sprig (htmx) components so they survive PJAX page transitions.
 *
 * On a full page load Sprig's own end-of-body script loads htmx, which
 * processes the document and wires every `data-hx-*` element. On a PJAX
 * navigation the router swaps `[data-view]` content in via `innerHTML`, so
 * that script never runs and the swapped-in nodes are never processed —
 * leaving e.g. the "load more" button dead until a hard reload.
 *
 * This block runs on every view enter for each `.c-sprig` container and:
 *   1. lazy-loads htmx if it isn't on the page yet (same 2.0.8 build Sprig
 *      ships, vendored at /assets/vendor/htmx.min.js), and
 *   2. ALWAYS calls `htmx.process()` on the container, which wires the
 *      `data-hx-*` attributes rendered by Sprig. Processing is idempotent:
 *      htmx marks initialized nodes, so full-page loads (where Sprig already
 *      processed everything) are unaffected.
 */

// Shared across instances so multiple .c-sprig blocks on one page trigger a
// single script load.
let htmxPromise = null

const loadHtmx = (src = '/assets/vendor/htmx.min.js') => {
  if (window.htmx) return Promise.resolve(window.htmx)

  if (!htmxPromise) {
    htmxPromise = new Promise((resolve, reject) => {
      // Sprig's inline config expects this global to exist before htmx runs.
      window.Sprig = window.Sprig || { components: [] }

      const script = document.createElement('script')
      script.src = src
      script.dataset.sprigSource = src

      // htmx sets `window.htmx` synchronously while executing.
      script.onload = () => {
        if (window.htmx) resolve(window.htmx)
        else reject(new Error(`htmx loaded from ${src} but window.htmx is not set`))
      }
      script.onerror = () => {
        htmxPromise = null // allow a retry on the next view enter
        reject(new Error(`Unable to load htmx from ${src}`))
      }

      document.head.appendChild(script)
    })
  }

  return htmxPromise
}

export class SprigComponent extends Block {
  async init() {
    super.init()

    try {
      const htmx = await loadHtmx()
      window.htmx = htmx
      // Wire this component's data-hx-* nodes (no-op for already-processed
      // nodes on full page loads).
      htmx.process(this.el)
    } catch (error) {
      console.warn('Failed to load HTMX/Sprig runtime:', error)
    }
  }
}
